// eslint-disable
import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { withNamespaces } from "react-i18next";

import styles from "./RouteRow.scss";
import routeStyles from "../Route/Route.scss";

import RouteToPoint from "../RouteToPointInfo";
import RouteFromPoint from "../RouteFromPointInfo";
import RouteRoadImage from "../RouteRoadImage";
import RoutePlaton from "../RoutePlaton";
import RouteRegionSeparator from "../RouteRegionSeparator";
import RouteCityExclude from "../RouteCityExclude";

import { getFormattedTimeSpan, getFormattedDistance } from "../../bl/routeRowService";
import { toThousandSeparatedNumber } from "../../utils";
import {getAddressParts} from "../../utils/addressHelper";

class RouteRow extends React.Component {
  getCityTitle = (citySize, t) => {
    const { key, defaultValue, lowerBound, upperBound } = citySizeTooltipMap[citySize];
    const populationTooltipPart = t(key, {
      defaultValue,
      lowerBound: toThousandSeparatedNumber(lowerBound),
      upperBound: toThousandSeparatedNumber(upperBound),
    });

    return `${populationTooltipPart}`;
  };

  render() {
    const {
      routeSegment,
      nextRouteSegment,
      handleExcludeCity,
      keyPointCountryIds,
      t,
    } = this.props;

    const {
      /** @type {RouteResponsePoint} */
      routePoint,
      /** @type {Country} */
      country,
      /** @type {Region} */
      region,
      hasToll,
      isCustom,
      citySize,
      isKeyPoint,
      segmentPlatonCost,
      traveledRoutes,
      trackNames
    } = routeSegment;
    
    const cityIconStyle = isCustom ? "customs" : `city-size-${citySize}`;
    const cityNameStyle = isCustom ? "customs" : `city-name-${citySize}`;
    const city = routePoint.city;
    const addressParts = getAddressParts(routePoint);
    const name = addressParts[0]; 

    const kmLabel = t("trace-common-units-kilometers-abbreviation", "км");
    const timeToNextFormatted = getFormattedTimeSpan(routeSegment.segmentDurationHours, t);
    const timeToFirstFormatted = getFormattedTimeSpan(routeSegment.totalDurationHours, t);
    
    const distanceToNextFormatted = getFormattedDistance(routeSegment.segmentDistance, kmLabel);
    const distanceToFirstFormatted = getFormattedDistance(routeSegment.totalDistance, kmLabel);

    const cityNameTitle = this.getCityTitle(citySize, t);
    /** @type {Country} */
    const nextCountry = nextRouteSegment?.country;
    /** @type {Region} */
    const nextRegion = nextRouteSegment?.region;
    
    const countryChanged = !!nextCountry && !!country && country.id !== nextCountry.id;
    const regionChanged = !!nextRegion && !!region && region.id !== nextRegion.id;
    const separatorText = countryChanged 
        ? nextCountry.name 
        : nextRegion 
            ? nextRegion.name 
            : "";
    const nextCountryId = countryChanged ? nextCountry.id : -1;
    const canExcludeCountry = !keyPointCountryIds.some(id => id === nextCountryId);

    const isLastPoint = traveledRoutes === undefined;

    const titleClasses = classnames(styles.cityName, styles[cityNameStyle], {
      [styles.bold]: isKeyPoint
    });

    return (
      <div className={styles.traceRowContainer}>
        <div className={styles.traceRow}>
          <div className={styles.traceImage}>
            <div className={classnames(styles.tracePoint, styles[cityIconStyle])} title={cityNameTitle} />
            { !isLastPoint && (
              <RouteRoadImage traveledRoutes={traveledRoutes} />
            )}
          </div>
          <div className={styles.traceDescription}>
            <div className={classnames(styles.cityRow, styles[cityNameStyle])}>
              <div className={titleClasses} title={cityNameTitle} role="button">
                { name }
              </div>
              {(!isKeyPoint && !isCustom) && city && (
                <RouteCityExclude
                  city={city}
                  onExcludeCity={handleExcludeCity}
                  tooltip={t("trace-route-exclude-point-tooltip", "Исключить из маршрута")}
                />
              )}
            </div>

            <RouteToPoint timeToFirst={timeToFirstFormatted} distanceToFirst={distanceToFirstFormatted} />

            {!isLastPoint && (
              <RouteFromPoint timeToNext={timeToNextFormatted} distanceToNext={distanceToNextFormatted} />
            )}
            {segmentPlatonCost > 0 && (
              <RoutePlaton platonCost={segmentPlatonCost} roadNames={trackNames} />
            )}
            {hasToll && <div className={styles.tollRoad}>{t("trace-common-road-toll", "Платная дорога")}</div>}
            {(regionChanged || countryChanged) && separatorText !== "" && (
              <RouteRegionSeparator
                canExcludeCountry={canExcludeCountry}
                countryHasChanged={countryChanged}
                separatorText={separatorText}
                countryId={nextCountryId}
                countryName={nextCountry.name}
              />
            )}
          </div>
        </div>
        <div className={routeStyles.backdrop} />
      </div>
    );
  }
}

RouteRow.propTypes = {
  handleExcludeCity: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const citySizeTooltipMap = {
  6: {
    key: "trace-route-city-tooltip-population-over-x",
    defaultValue: "Население свыше {{lowerBound}} человек.",
    lowerBound: 1000000,
    upperBound: null,
  },
  5: {
    key: "trace-route-city-tooltip-population-between-x-and-y",
    defaultValue: "Население от {{lowerBound}} до {{upperBound}} человек.",
    lowerBound: 500000,
    upperBound: 1000000,
  },
  4: {
    key: "trace-route-city-tooltip-population-between-x-and-y",
    defaultValue: "Население от {{lowerBound}} до {{upperBound}} человек.",
    lowerBound: 100000,
    upperBound: 500000,
  },
  3: {
    key: "trace-route-city-tooltip-population-between-x-and-y",
    defaultValue: "Население от {{lowerBound}} до {{upperBound}} человек.",
    lowerBound: 50000,
    upperBound: 100000,
  },
  2: {
    key: "trace-route-city-tooltip-population-between-x-and-y",
    defaultValue: "Население от {{lowerBound}} до {{upperBound}} человек.",
    lowerBound: 10000,
    upperBound: 50000,
  },
  1: {
    key: "trace-route-city-tooltip-population-up-to-x",
    defaultValue: "Население до {{upperBound}} человек.",
    lowerBound: null,
    upperBound: 10000,
  },
  0: {
    key: "trace-route-city-tooltip-population-up-to-x",
    defaultValue: "Население до {{upperBound}} человек.",
    lowerBound: null,
    upperBound: 10000,
  },
};

export default withNamespaces()(RouteRow);
